import React, { useCallback, useRef } from 'react';
import { useController } from 'react-hook-form';

/**
 * @typedef {import('../../../../types/provider').Provider} Provider
 */

import { searchProvidersByName } from '../../../../utils/serviceCalls/provider';
import AsyncSearchCombobox from '../../../Shared/SearchCombobox/AsyncSearchCombobox';
import { VALIDATION_MESSAGES } from '../../constants';
import { FIELDS } from '../constants';
import FieldWrapper from './FieldWrapper';
import { useFormField } from './hooks';

/**
 * A self-contained name search field that:
 * - Uses useController for custom component integration
 * - Provides async search with debouncing (handled by AsyncSearchCombobox)
 * - Displays its own error state
 * - Shows API errors when search fails
 *
 * @param {Object} props
 * @param {string} [props.locationCity] - City name filter for search (e.g., "Denver")
 * @param {(provider: Provider|null) => void} props.onProviderUpdate - Called when provider changes
 * @param {boolean} [props.disabled] - Whether the field is disabled
 */
export default function NameField({
  locationCity,
  onProviderUpdate,
  disabled,
}) {
  const { control, validateField, errorMessage } = useFormField(
    FIELDS.name,
    disabled,
  );

  // Ref for API errors (same pattern as NpiField/Last4Field)
  // Using useRef so validation can read current error without re-registering
  const apiErrorRef = useRef(/** @type {string|null} */ (null));

  // useController is the hook version of Controller - better for custom components
  const {
    field: { value, onChange, onBlur, ref },
  } = useController({
    name: FIELDS.name,
    control,
    rules: {
      validate: () => {
        if (disabled) return true;
        if (apiErrorRef.current) return apiErrorRef.current;
        return true;
      },
    },
  });

  const fetchProviders = useCallback(
    /**
     * @param {string} query - User's search text
     * @returns {Promise<Provider[]>} - List of matching providers
     */
    async (query) => {
      try {
        const response = await searchProvidersByName(query, locationCity);

        if (!response.success) {
          apiErrorRef.current = VALIDATION_MESSAGES.SEARCH_ERROR;
          validateField();
          return [];
        }

        // Clear any previous error on success
        apiErrorRef.current = null;
        return response.results || [];
      } catch (_e) {
        apiErrorRef.current = VALIDATION_MESSAGES.SEARCH_ERROR;
        validateField();
        return [];
      }
    },
    [locationCity, validateField],
  );

  /**
   * Handle provider selection from combobox
   * @param {Provider|null} selectedProvider
   */
  const handleChange = (selectedProvider) => {
    // Clear any API error when user makes a selection
    apiErrorRef.current = null;
    // Update form with Provider's name
    onChange(selectedProvider?.displayName || '');
    // Tell parent about the selection
    onProviderUpdate(selectedProvider);
  };

  return (
    <FieldWrapper label="Name" errorMessage={errorMessage}>
      <AsyncSearchCombobox
        name={FIELDS.name}
        value={value}
        onChange={handleChange}
        onBlur={onBlur}
        fetchOptionsFunction={fetchProviders}
        getOptionLabel={(p) => {
          if (!p) return '';
          const displayName = p.displayName || '';
          const location = p.location || '';
          return location ? `${displayName} - ${location}` : displayName;
        }}
        getOptionKey={(p) => `name-${p?.providerCode}`}
        placeholder="Provider's Name"
        showError={!!errorMessage}
        emptyText="No results"
        ref={ref}
        disabled={disabled}
      />
    </FieldWrapper>
  );
}
